import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, CircleAlert, Loader2, ShieldCheck, XCircle } from 'lucide-react';
import { apiService } from '../services/apiService';
import { TenderParticipationApproval } from '../types';
import { useModal } from './ModalProvider';

type ApprovalContext = {
  required: boolean;
  threshold: number;
  blockers: string[];
  readiness: string;
  approval: TenderParticipationApproval | null;
};

const currency = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const TenderParticipationApprovalPanel: React.FC<{ tenderId: string }> = ({ tenderId }) => {
  const [context, setContext] = useState<ApprovalContext | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showAlert } = useModal();
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null') as { id?: string; role?: string } | null; } catch { return null; }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setContext(await apiService.getParticipationApproval(tenderId));
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível carregar a aprovação.', 'Erro');
    } finally {
      setLoading(false);
    }
  }, [showAlert, tenderId]);

  useEffect(() => { void load(); }, [load]);

  const requestApproval = async () => {
    try {
      setSaving(true);
      await apiService.requestParticipationApproval(tenderId, notes || undefined);
      setNotes('');
      await load();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível solicitar a aprovação.', 'Erro');
    } finally {
      setSaving(false);
    }
  };

  const decide = async (status: 'APPROVED' | 'REJECTED') => {
    try {
      setSaving(true);
      await apiService.decideParticipationApproval(tenderId, status, notes || undefined);
      setNotes('');
      await load();
    } catch (error) {
      await showAlert(error instanceof Error ? error.message : 'Não foi possível registrar a decisão.', 'Erro');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className="min-h-[96px] grid place-items-center rounded-lg border border-line bg-surface dark:border-line-dark dark:bg-surface-dark"><Loader2 className="h-5 w-5 animate-spin text-brand-600" /></section>;
  if (!context) return null;

  const approval = context.approval;
  const canDecide = Boolean(approval?.status === 'PENDING' && currentUser && ['ADMIN', 'MANAGER'].includes(currentUser.role || '') && approval.requestedById !== currentUser.id);
  const status = approval?.status || 'NOT_REQUIRED';
  const meta = status === 'APPROVED'
    ? { label: 'Aprovação concedida', className: 'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300', icon: CheckCircle2 }
    : status === 'REJECTED'
      ? { label: 'Participação reprovada', className: 'border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300', icon: XCircle }
      : status === 'PENDING'
        ? { label: 'Aguardando aprovação', className: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300', icon: CircleAlert }
        : { label: 'Sem alçada adicional', className: 'border-line bg-surface-alt text-ink-subtitle dark:border-line-dark dark:bg-surface-dark-alt dark:text-ink-subtitle-dark', icon: ShieldCheck };
  const Icon = meta.icon;

  return <section className="rounded-lg border border-line bg-surface p-5 dark:border-line-dark dark:bg-surface-dark">
    <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div><div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand-600" /><h3 className="font-semibold text-ink-title dark:text-ink-title-dark">Alçada de participação</h3></div><p className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">Obrigatória para prioridade Ultra ou valor igual/superior a {currency(context.threshold)}.</p></div>
      <span className={`inline-flex w-fit items-center gap-1 rounded border px-2 py-1 text-xs font-medium ${meta.className}`}><Icon className="h-3.5 w-3.5" />{meta.label}</span>
    </header>

    {context.blockers.length > 0 && <div className="mt-4 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/25 dark:text-rose-200"><p className="font-medium">Pendências para solicitar participação</p><ul className="mt-1 list-disc pl-4 text-xs">{context.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul></div>}
    {approval && <div className="mt-4 text-sm text-ink-subtitle dark:text-ink-subtitle-dark"><p>Solicitada por <strong className="text-ink-title dark:text-ink-title-dark">{approval.requestedByName || 'Sistema'}</strong> em {new Date(approval.requestedAt).toLocaleString('pt-BR')}.</p>{approval.decisionByName && <p className="mt-1">Decidida por <strong className="text-ink-title dark:text-ink-title-dark">{approval.decisionByName}</strong>{approval.decisionAt ? ` em ${new Date(approval.decisionAt).toLocaleString('pt-BR')}` : ''}.</p>}{approval.decisionNotes && <p className="mt-2 rounded border border-line p-2 text-xs dark:border-line-dark">{approval.decisionNotes}</p>}</div>}

    {context.required && status !== 'APPROVED' && <div className="mt-4 border-t border-line pt-4 dark:border-line-dark"><textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={canDecide && status === 'REJECTED' ? 'Motivo da reprovação (obrigatório)' : 'Observação para a alçada (opcional)'} className="min-h-[64px] w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink-title dark:border-line-dark dark:bg-surface-dark dark:text-ink-title-dark" />
      <div className="mt-3 flex flex-wrap gap-2">{!approval && <button type="button" disabled={saving || context.blockers.length > 0} onClick={() => void requestApproval()} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-medium text-white disabled:opacity-50">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Solicitar aprovação</button>}{approval?.status === 'REJECTED' && <button type="button" disabled={saving || context.blockers.length > 0} onClick={() => void requestApproval()} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-brand-600 px-3 text-sm font-medium text-brand-700 dark:text-brand-300 disabled:opacity-50">Solicitar nova análise</button>}{canDecide && <><button type="button" disabled={saving} onClick={() => void decide('APPROVED')} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-emerald-600 px-3 text-sm font-medium text-emerald-700 dark:text-emerald-300 disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> Aprovar</button><button type="button" disabled={saving} onClick={() => void decide('REJECTED')} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-rose-600 px-3 text-sm font-medium text-rose-700 dark:text-rose-300 disabled:opacity-50"><XCircle className="h-4 w-4" /> Reprovar</button></>}</div>
    </div>}
  </section>;
};

export default TenderParticipationApprovalPanel;
